import React, { useState, useEffect } from 'react';
import { Clock, Edit3, Wrench, User as UserIcon, Search, X, Calendar, Timer } from 'lucide-react';
import DateTimeInput from 'components/common/DateTimeInput';
import Modal from 'components/common/Modal';
import ClockIn from 'models/ClockIn';
import RepairOrder from 'models/RepairOrderModel';
import config from 'helpers/config';
import axios from 'helpers/axios';
import moment from 'moment';
import CustomerFullName from 'components/common/CustomerFullName';
import getFullVehicleName from 'helpers/vehicleFullNameHelper';

interface Props {
    timeLog: ClockIn;
    handleClose: () => void;
    activeRepairOrders?: RepairOrder[];
}

const TimeLogModal = ({ timeLog, handleClose, activeRepairOrders = [] }: Props) => {
    // Use useState to create local state for timeLog
    const [localTimeLog, setLocalTimeLog] = useState<ClockIn>(timeLog);
    const [isROSearchExpanded, setIsROSearchExpanded] = useState(false);
    const [roSearchQuery, setROSearchQuery] = useState('');

    // Filter repair orders based on search query
    const filteredRepairOrders = activeRepairOrders.filter(
        (ro) =>
            ro.repairOrderNumber?.toString().toLowerCase().includes(roSearchQuery.toLowerCase()) ||
            ro.title?.toLowerCase().includes(roSearchQuery.toLowerCase()) ||
            ro.customer?.firstName?.toLowerCase().includes(roSearchQuery.toLowerCase()) ||
            ro.customer?.lastName?.toLowerCase().includes(roSearchQuery.toLowerCase()),
    );

    // Function to update the local timeLog
    const handleTimeChange = (newValue: any, property: 'clockInTime' | 'clockOutTime' | 'description') => {
        if (newValue === 'Invalid date') {
            setLocalTimeLog((prevTimeLog) => ({
                ...prevTimeLog,
            }));
        } else {
            setLocalTimeLog((prevTimeLog) => ({
                ...prevTimeLog,
                [property]: newValue,
            }));
        }
    };

    // Function to update repair order
    const handleRepairOrderChange = (repairOrder: RepairOrder | null) => {
        setLocalTimeLog((prevTimeLog) => ({
            ...prevTimeLog,
            repairorderId: repairOrder?.id || null,
            repairorder: repairOrder,
        }));
        setIsROSearchExpanded(false);
        setROSearchQuery('');
    };

    // Calculate duration
    const getDuration = () => {
        if (localTimeLog.clockInTime && localTimeLog.clockOutTime) {
            const durationMilliseconds = moment
                .duration(moment(localTimeLog.clockOutTime).diff(moment(localTimeLog.clockInTime)))
                .as('milliseconds');
            const durationDays = Math.floor(durationMilliseconds / (1000 * 60 * 60 * 24));
            const remainingMilliseconds = durationMilliseconds % (1000 * 60 * 60 * 24);

            return `${durationDays > 0 ? `${durationDays} day${durationDays > 1 ? 's' : ''}, ` : ''}${moment
                .utc(remainingMilliseconds)
                .format('HH:mm:ss')}`;
        }
        return '00:00:00';
    };

    // Function to update the timeLog in the database
    const handleUpdate = () => {
        axios
            .put(`${config.apiUrl}/timeLog`, localTimeLog)
            .then(handleClose)
            .catch((_) => console.log(_));
    };

    const isTimeInvalid = moment(localTimeLog?.clockOutTime).isBefore(localTimeLog?.clockInTime);

    return (
        <Modal
            isOpen={true}
            onClose={handleClose}
            headerText="Edit Time Log Session"
            onSave={handleUpdate}
            isSaveDisabled={isTimeInvalid}
        >
            <div className="space-y-6">
                {/* Session Overview */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center space-x-2 mb-2">
                        <Timer className="h-5 w-5 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-900">Session Duration</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-900 font-mono">{getDuration()}</div>
                </div>

                {/* Time Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Clock In */}
                    <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-green-600" />
                            <label className="text-sm font-semibold text-gray-700">Clock In Time</label>
                        </div>
                        <DateTimeInput
                            handleChange={(newValue) => handleTimeChange(newValue, 'clockInTime')}
                            dateTime={localTimeLog?.clockInTime}
                            id={localTimeLog.id + 'in'}
                            isInvalid={isTimeInvalid}
                        />
                    </div>

                    {/* Clock Out */}
                    <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-red-600" />
                            <label className="text-sm font-semibold text-gray-700">Clock Out Time</label>
                        </div>
                        <DateTimeInput
                            handleChange={(newValue) => handleTimeChange(newValue, 'clockOutTime')}
                            dateTime={localTimeLog?.clockOutTime}
                            id={localTimeLog.id + 'out'}
                            isInvalid={isTimeInvalid}
                        />
                        {isTimeInvalid && (
                            <div className="flex items-center space-x-1 text-red-600 text-sm">
                                <X className="h-3 w-3" />
                                <span>Clock out time must be after clock in time</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Repair Order Section */}
                <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                        <Wrench className="h-4 w-4 text-gray-600" />
                        <label className="text-sm font-semibold text-gray-700">Associated Repair Order</label>
                    </div>

                    {localTimeLog?.repairorder ? (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Wrench className="h-4 w-4 text-blue-600" />
                                        <span className="text-sm font-bold text-blue-900">
                                            #{localTimeLog.repairorder.repairOrderNumber}
                                        </span>
                                        {localTimeLog.repairorder.title && (
                                            <span className="text-sm font-semibold text-gray-700">
                                                {localTimeLog.repairorder.title}
                                            </span>
                                        )}
                                    </div>

                                    {localTimeLog.repairorder.vehicle && (
                                        <div className="flex items-center space-x-1 text-gray-600 mb-1">
                                            <span className="text-sm">
                                                {getFullVehicleName(localTimeLog.repairorder.vehicle)}
                                            </span>
                                        </div>
                                    )}

                                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                                        <UserIcon className="h-3 w-3" />
                                        <CustomerFullName
                                            customer={localTimeLog.repairorder.customer}
                                            customerId={localTimeLog.repairorder.customer?.id}
                                        />
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleRepairOrderChange(null)}
                                    className="p-1 text-blue-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Remove repair order"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="relative">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                <input
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="Search and select a repair order..."
                                    value={roSearchQuery}
                                    onChange={(e) => setROSearchQuery(e.target.value)}
                                    onFocus={() => setIsROSearchExpanded(true)}
                                    onBlur={() => {
                                        setTimeout(() => {
                                            setIsROSearchExpanded(false);
                                        }, 200);
                                    }}
                                />
                            </div>

                            {isROSearchExpanded && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                                        <span className="text-xs font-semibold text-gray-600">
                                            {filteredRepairOrders.length} repair order(s) found
                                        </span>
                                    </div>
                                    {filteredRepairOrders.length > 0 ? (
                                        filteredRepairOrders.map((ro, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleRepairOrderChange(ro)}
                                                className="w-full px-3 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center space-x-2 mb-2">
                                                            <span className="text-sm font-bold text-green-600">
                                                                #{ro.repairOrderNumber}
                                                            </span>
                                                            {ro.title && (
                                                                <span className="text-sm font-semibold text-gray-700 truncate">
                                                                    {ro.title}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {ro.vehicle && (
                                                            <div className="flex items-center space-x-1 text-gray-600 mb-1">
                                                                <span className="text-sm">
                                                                    {getFullVehicleName(ro.vehicle)}
                                                                </span>
                                                            </div>
                                                        )}

                                                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                                                            <UserIcon className="h-3 w-3" />
                                                            <CustomerFullName
                                                                customer={ro.customer}
                                                                customerId={ro.customer?.id}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        ))
                                    ) : (
                                        <div className="px-3 py-6 text-center text-gray-500">
                                            <Wrench className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                                            <p className="text-sm">No repair orders found</p>
                                            {roSearchQuery && (
                                                <p className="text-xs text-gray-400">Try a different search term</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Notes Section */}
                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Edit3 className="h-4 w-4 text-gray-600" />
                        <label className="text-sm font-semibold text-gray-700">Session Notes</label>
                    </div>
                    <textarea
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                        placeholder="Add notes about the work performed during this session..."
                        onChange={(e) => handleTimeChange(e.target.value, 'description')}
                        value={localTimeLog?.description || ''}
                        rows={4}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default TimeLogModal;
